/**
 * 이벤트 전송 계층 — 목과 실 WS 를 하나의 인터페이스 뒤로 숨긴다.
 *
 * 상위(useRoomStream)는 여기서 나오는 Envelope 만 본다. 따라서
 * `USE_MOCK` 한 줄이 목/실서버를 가르며, 화면 코드에는 목 흔적이 없다.
 *
 * §9 호스트 세션 상태 머신:
 *   CONNECTING → AUTH(서브프로토콜 키 검증) → ACTIVE
 *   → RECONNECTING(지수 백오프 1→8s) → TERMINATED
 */

import { isEnvelope, type Envelope, type PongData } from './events'
import { API_BASE, keyProtocol, USE_MOCK, wsPath, wsUrl } from './config'

export type ConnState =
  | 'connecting'
  | 'active'
  | 'reconnecting'
  | 'terminated'
  /** §9 — 같은 host_key 의 두 번째 연결이 이 소켓을 대체했다 */
  | 'replaced'
  /**
   * 방이 사라졌다(서버 재시작·명시 종료·TTL). 재시도는 의미가 없다 —
   * 실측: 서버를 재배포하자 방이 사라졌는데 SPA 가 403 을 받으며 **무한히**
   * 재접속을 시도했고, 사용자는 "통역이 안 된다"만 보았다.
   */
  | 'room_gone'
  /**
   * 방이 **종료됐다는 증거가 있다**(DEF-018 §5.3) — 참가자 전용
   * `/liveness` 가 200 으로 `state:'closed'` 를 명시 반환했다.
   *
   * `room_gone` 과 나누는 이유: 그쪽은 재접속 소진·판정 불가도 포함하고,
   * `roomLiveness` 는 네트워크 오류·비200 에서 `unknown` 을 반환하므로
   * 서버가 잠깐 불통이어도 `room_gone` 이 된다. 그때 "회의가 종료됐습니다"
   * 라고 말하면 **거짓말**이고, 사용자는 살아 있는 회의를 포기한다.
   */
  | 'room_closed'

export interface TransportHandlers {
  onEvent: (envelope: Envelope) => void
  onState: (state: ConnState) => void
  /**
   * A1 — 서버가 **실제로 받은 것**의 보고(호스트 소켓 전용).
   *
   * 방 이벤트가 아니라 리듀서를 태우지 않는다. 화면이 "서버가 듣고 있는
   * 소리 크기" 를 클라 미터와 나란히 보여 줄 수 있게 여기로 뺀다 —
   * 두 값이 갈리는 순간이 곧 마이크/업링크 고장의 서명이다.
   */
  onPong?: (pong: PongData) => void
  /**
   * A.6 — 서버가 `server.caps` 로 알린 능력. `audio_otf1 === true` 를
   * 관측하면 상위(capture.ts)가 이후 프레임을 OTF1 로 전환할 수 있다.
   * 이 envelope 은 §8.1 공통 envelope 계약(v/room/seq)을 따르지 않는
   * 서버 능력 통지이므로 `isEnvelope`/`onEvent` 를 거치지 않고 따로 알린다.
   */
  onCaps?: (caps: { audio_otf1: boolean; vad_energy_floor_dbfs?: number }) => void
}

export interface Transport {
  /** 오디오 프레임 송신(§4) — 뷰어 소켓은 인바운드 전면 거부(§10.1)라 no-op */
  send(data: ArrayBuffer): void
  /**
   * 호스트 제어 메시지(§4 뮤트 등) — 텍스트 프레임.
   *
   * 뮤트는 캡처 중단이 정본이지만, 서버도 알아야 한다: 말하다 끊긴 발화를
   * 버리지 않고 flush 하려면(§5 리셋 조건) "이제 안 온다" 는 신호가 필요하다.
   * 신호가 없으면 서버는 그냥 조용한 구간으로 보고 적응 무음 타임아웃까지
   * 기다린다 — 뮤트 직전 발화가 그만큼 늦게 뜬다.
   */
  sendControl(message: object): void
  /**
   * §8.5 회의 중 채팅 — 뷰어도 보낼 수 있는 유일한 인바운드 메시지.
   * `name` 은 **첫 메시지에만** 실으면 된다(서버가 소켓 단위로 기억한다).
   */
  sendChat(body: string, name?: string): void
  readonly bufferedAmount: number
  readonly ready: boolean
  /**
   * A.6 — 이 소켓이 지금 OTF1(FLAC 압축)로 전송해도 되는가. **소켓
   * 클로저 변수**로만 산다(모듈 스코프·React state·storage 금지) — onclose
   * 에서 false 로 리셋되고 재접속마다 새로 판정된다. pongSupported 와 달리
   * 재접속을 넘어 유지하면 안 된다: 롤백된 서버(캡을 모름)에 stale-true 로
   * 붙으면 첫 OTF1 프레임이 FrameError→1003 을 부르고, 재시도마다 같은
   * 일이 반복돼 실황 발화가 매번 끊긴다.
   */
  readonly otf1Armed: boolean
  close(): void
}

export interface ConnectOptions {
  room: string
  /**
   * 접속 자격 — 서명 토큰(§5.2) 또는 전환기 host_key/view_key.
   *
   * **함수로 주면 재접속마다 다시 평가한다**: 토큰은 TTL 10분이라, 고정
   * 문자열을 캡처해 두면 긴 회의의 두 번째 재접속부터 만료 토큰으로
   * 핸드셰이크한다. RoomAuth.current 를 그대로 넘기는 것이 정상 경로다.
   */
  key: string | (() => string)
  role: 'host' | 'viewer'
  /** §8.3 재접속 커서 — 마지막으로 본 seq */
  since?: number
  /**
   * 자격 거부(4401) 시 재교환 콜백(lib/token.ts RoomAuth.refresh).
   *
   * 새 자격을 돌려주면 그걸로 **한 번** 다시 붙어 본다 — 만료 경계에 걸린
   * 재접속(절전 복귀 등)이 사용자 개입 없이 살아나는 경로다. null 이면
   * 더 시도할 자격이 없다는 뜻이라 terminated 로 종결한다(조용한 죽은
   * UI 금지 — 화면이 배너로 말한다).
   */
  refreshKey?: () => Promise<string | null>
}

/** §9 지수 백오프 1→8s */
function backoffMs(attempt: number): number {
  return Math.min(8_000, 1_000 * 2 ** Math.min(attempt, 3))
}

/**
 * 재접속 시도 상한. 무한 재시도는 죽은 방을 향해 403 폭풍을 만든다(실측:
 * 재배포 후 수십 건의 거부된 핸드셰이크가 로그를 채웠다). 상한에 닿으면
 * 사람이 읽을 수 있는 종료 상태로 넘어간다.
 */
const MAX_RECONNECT_ATTEMPTS = 8

/**
 * ── 반쯤 열린 소켓 감지(2026-08-05 라이브 진단) ──────────────────────────
 *
 * TCP 는 경로 중간의 장비가 조용히 연결을 버려도 양끝에 알려 주지 않는다.
 * 그러면 브라우저의 `readyState` 는 **영원히 OPEN 이고**, `send()` 는 예외도
 * 안 던지며, `onclose` 는 오지 않는다. 오디오는 허공으로 나간다. nginx 는
 * 3600s 읽기 타임아웃이라 최악의 경우 한 시간을 그렇게 보낸다 — 신고된
 * "멍때린다" 의 정체이고, 새로고침이 고치는 이유(소켓을 새로 맺으니까)다.
 *
 * 서버는 5초마다 room.status 를 방송한다(§8.2 status 티커). 따라서 **아무
 * 메시지도 안 오는 구간이 길다는 것 자체가 소켓이 죽었다는 신호**다. 별도
 * 프로토콜 추가 없이 기존 하트비트를 수신 감시로 재활용한다.
 */
const SILENCE_TIMEOUT_MS = 20_000
/** 수신 감시 주기 */
const WATCH_INTERVAL_MS = 2_000

/**
 * 앱 레벨 ping 주기. WebSocket 프로토콜 ping 은 **중간 장비가 대신 응답할 수
 * 있어** 종단 생존을 증명하지 못한다. 그래서 우리가 만든 텍스트 프레임을
 * 쓴다.
 */
const PING_INTERVAL_MS = 5_000

/**
 * ── A1: pong 이 끊기면 **업링크가 죽은 것**이다 ──────────────────────────
 *
 * 위 `SILENCE_TIMEOUT_MS` 판정의 구멍: `lastRecvAt` 는 **무엇이든** 받으면
 * 갱신되는데, 서버는 5초마다 방 전체에 `room.status` 를 쏜다. 따라서
 * 다운링크(서버→브라우저)만 살아 있으면 **업링크(브라우저→서버)가 죽어도**
 * 그 값은 영원히 신선하다. 실측 장애 A 가 정확히 이 모양이었다 — 호스트는
 * 말하고 있었고 화면은 정상이었는데 서버에는 아무것도 도착하지 않았다.
 *
 * pong 은 우리가 보낸 ping 에만 응답하므로 **이 소켓의 업링크가 살아 있을
 * 때만** 돌아온다. 그래서 생존 판정을 pong 전용 시계로 따로 둔다.
 *
 * 여유는 넉넉하게 잡는다(5초 주기 × 4회분). 모델이 GPU 를 물고 있는 순간
 * 이벤트 루프가 잠깐 밀릴 수 있는데, 그걸 소켓 사망으로 오판해 재접속하면
 * 진행 중 발화가 끊긴다 — **오판 끊김이 늦은 감지보다 해롭다**.
 */
const PONG_TIMEOUT_MS = 20_000

/**
 * 방이 아직 살아 있는지 확인한다 — **참가자 전용** `/liveness`(이 방의
 * 자격을 Bearer 로 내민다, sec DYN-05).
 *
 * 공개 `/entry` 를 쓰지 않는 이유: 그 응답은 이제 비공개 방을 없는 방과
 * 똑같이 '닫힘' 으로 답한다(존재 미누출). 그것을 종료 증거로 읽으면 살아
 * 있는 비공개 회의가 재접속 대신 room_closed 로 끝난다.
 *
 * 세 값이다(DEF-018 §5.3): `'closed'` 는 **종료 증거**(200 + `state:'closed'`),
 * `'alive'` 는 살아 있음, `'unknown'` 은 판정 불가(네트워크 오류·비200 —
 * 만료 토큰의 403 도 여기)다. 판정 불가를 `alive` 와 합치던 예전 불리언이
 * 정확히 문제였다 — 서버가 잠깐 불통인 것과 회의가 끝난 것을 화면이
 * 구별하지 못했다.
 */
async function roomLiveness(room: string, key: string): Promise<'alive' | 'closed' | 'unknown'> {
  try {
    const res = await fetch(`${API_BASE}/rooms/${encodeURIComponent(room)}/liveness`, {
      credentials: 'same-origin',
      headers: { Authorization: `Bearer ${key}` },
    })
    if (!res.ok) return 'unknown'
    const body = (await res.json()) as { state?: string }
    return body.state === 'closed' ? 'closed' : 'alive'
  } catch {
    return 'unknown'
  }
}

/**
 * 방 이벤트 스트림에 접속한다. 반환된 Transport 를 close() 할 때까지
 * 끊기면 자동 재접속하며, 재접속 시 `?since=` 로 누락분을 요구한다(§8.3).
 */
export async function connect(
  options: ConnectOptions,
  handlers: TransportHandlers,
): Promise<Transport> {
  if (USE_MOCK) {
    // 목은 dev 전용 — 동적 import 라 프로덕션 번들에 들어가지 않는다.
    const { connectMock } = await import('../mock/client')
    return connectMock(options, handlers)
  }
  return connectReal(options, handlers)
}

/**
 * 실 WS 접속. **테스트 표면으로도 노출한다**(A1): 생존 판정은 타이머와
 * 소켓 상태가 얽힌 로직이라 `connect()` 의 목 분기를 거치면 검증할 수 없고,
 * 이 판정이 틀리면 멀쩡한 회의가 재접속 루프에 빠진다 — 목으로는 절대
 * 잡히지 않는 종류의 사고다.
 */
export function connectReal(options: ConnectOptions,
                            handlers: TransportHandlers): Transport {
  let socket: WebSocket | null = null
  let closed = false
  let attempt = 0
  let lastSeq = options.since ?? 0
  let timer: ReturnType<typeof setTimeout> | null = null
  /** 마지막으로 **무엇이든** 수신한 시각 — 다운링크 사망 판정의 근거 */
  let lastRecvAt = Date.now()
  /**
   * 마지막 **pong** 수신 시각 — 업링크 사망 판정의 근거(A1).
   * `lastRecvAt` 와 반드시 분리해야 한다: 저것은 5초 status 티커가 계속
   * 갱신하므로 업링크 사망을 영원히 가린다.
   */
  let lastPongAt = 0
  /**
   * 이 서버가 pong 을 아는가.
   *
   * **배포 순서 위험**(문서에 명시된 것): ACK 타임아웃이 서버보다 **먼저**
   * 나가면, pong 을 모르는 구버전 서버에 붙은 클라가 20초마다 스스로 소켓을
   * 끊는다 — 멀쩡한 회의가 끝없는 재접속 루프에 빠진다. 새 판정은 **pong 을
   * 한 번이라도 본 뒤에만** 켠다. 한 번도 못 봤으면 구버전 서버로 보고
   * 기존 `lastRecvAt` 판정만 쓴다(예전과 정확히 같은 거동).
   */
  let pongSupported = false
  let watch: ReturnType<typeof setInterval> | null = null
  /**
   * 4401 재교환을 이미 써 버렸는가 — 서버가 프레임을 하나라도 내주면
   * (자격 수용의 증거) 다시 쓸 수 있게 리셋한다. 이 가드가 없으면
   * "재교환 → 또 4401 → 또 재교환" 루프가 죽은 자격으로 서버를 두드린다.
   */
  let authRetried = false
  /**
   * A.6 — per-socket, never persisted(§ Transport.otf1Armed 주석 참고).
   * onopen 에서도 명시적으로 false 로 되돌린다 — 재접속한 새 소켓은
   * 아직 이 소켓에서 캡을 관측하지 못했기 때문이다.
   */
  let otf1Armed = false

  /**
   * 서버 하트비트가 끊긴 소켓을 강제로 끊는다. `close()` 를 부르면 브라우저가
   * `onclose` 를 내주므로, 아래의 기존 재접속 경로가 그대로 이어받는다.
   */
  const startWatch = (): void => {
    if (watch) clearInterval(watch)
    let lastPingAt = 0
    watch = setInterval(() => {
      if (closed || !socket) return
      if (socket.readyState !== WebSocket.OPEN) return
      const now = Date.now()
      // 능동 확인 — 조용한 방(자막도 채팅도 없음)에서도 경로를 찔러 본다.
      //
      // **호스트 소켓에서만** 보낸다. 뷰어 인바운드는 §10.1 전면 거부라
      // 채팅 외 텍스트가 감사 계수(viewer_rejects)를 올리고 채팅 레이트리밋
      // 예산까지 쓴다 — 생존 확인이 보안 경보를 울려서는 안 된다. 뷰어는
      // 서버의 5초 status 티커만으로 충분히 생존을 판정할 수 있다(수신 감시).
      if (options.role === 'host' && now - lastPingAt >= PING_INTERVAL_MS) {
        lastPingAt = now
        try {
          socket.send(JSON.stringify({ type: 'ping', t: now }))
        } catch {
          // send 가 던진다면 이미 죽은 소켓이다 — 아래 판정이 곧 끊는다.
        }
      }
      // ── 업링크 사망 판정(A1) ──────────────────────────────────────────
      // pong 을 한 번이라도 본 소켓에서만 켠다(배포 순서 안전장치). 구버전
      // 서버에서는 이 블록이 통째로 비활성이라 예전 거동 그대로다.
      if (options.role === 'host' && pongSupported &&
          now - lastPongAt >= PONG_TIMEOUT_MS) {
        // 다운링크는 살아 있을 수 있다(status 티커가 계속 온다). 그래도
        // 끊는다 — 올라가지 않는 오디오는 통역되지 않고, 그 사실을 아무도
        // 모르는 것이 이 장애의 본질이었다.
        lastPongAt = now
        lastRecvAt = now
        socket.close(4000, 'pong timeout')
        return
      }
      if (now - lastRecvAt < SILENCE_TIMEOUT_MS) return
      // 죽었다고 판정 — 우리 쪽에서 끊어 재접속을 시작시킨다.
      lastRecvAt = now
      socket.close(4000, 'heartbeat timeout')
    }, WATCH_INTERVAL_MS)
  }

  const open = (): void => {
    if (closed) return
    handlers.onState(attempt === 0 ? 'connecting' : 'reconnecting')

    const query = lastSeq > 0 ? `?since=${lastSeq}` : ''
    // 경로 정본은 서버 + 배포 nginx 다(§10.1) — /host/ws · /relay/ws.
    const url = wsUrl(`${wsPath(options.role, options.room)}${query}`)
    // 자격은 **시도 시점에** 평가한다 — 토큰(TTL 10분)은 재접속 사이에
    // 갱신되어 있을 수 있고, 그 최신본이 이 핸드셰이크에 실려야 한다.
    const key = typeof options.key === 'function' ? options.key() : options.key
    // §10.1 — 키는 쿼리가 아니라 서브프로토콜로 전달(로그·프록시 누출 방지)
    const ws = new WebSocket(url, [keyProtocol(key)])
    ws.binaryType = 'arraybuffer'
    socket = ws

    ws.onopen = () => {
      attempt = 0
      lastRecvAt = Date.now()
      // 새 소켓은 아직 pong 을 못 받았다 — 시계를 지금으로 밀어 두지 않으면
      // 첫 ping 이 나가기도 전에 타임아웃으로 판정된다.
      lastPongAt = Date.now()
      // A.6 — 새 소켓은 이 소켓에서 아직 캡을 못 봤다. per-socket 이므로
      // 여기서 다시 false 로 못박는다(재접속 사이에 새지 않게).
      otf1Armed = false
      // `pongSupported` 는 **재접속을 넘어 유지한다**: 이 서버가 pong 을 안다는
      // 사실은 소켓이 아니라 서버의 성질이다. 초기화하면 재접속마다 판정이
      // 한 번씩 꺼져 half-open 이 그 창으로 빠져나간다.
      startWatch()
      handlers.onState('active')
    }

    ws.onmessage = (event: MessageEvent) => {
      // 파싱 성공 여부와 무관하게 **도착했다는 사실**이 생존 증거다.
      lastRecvAt = Date.now()
      // 서버가 프레임을 내줬다 = 이 자격이 수용됐다 — 4401 재교환 예산 리셋.
      authRetried = false
      if (typeof event.data !== 'string') return
      let parsed: unknown
      try {
        parsed = JSON.parse(event.data)
      } catch {
        return // fail-closed — 깨진 프레임은 조용히 버린다(§6.4는 서버측 보장)
      }
      // A.6 — server.caps 는 서버가 Event.to_wire 로 완전한 envelope
      // (v:1, room, seq:null, ts, data) 로 실어 보낸다 — isEnvelope 를
      // 통과한다. 그래도 방 이벤트가 아니므로 리듀서에 흘리지 않으려고
      // isEnvelope 가드 **앞에서** 가로챈다(pong 과 같은 취급). 이 인터셉트가
      // 없는 구클라에서는 envelope 가 그대로 리듀서까지 가지만, 리듀서의
      // default 분기가 알 수 없는 type 을 무시하므로 안전하다(배포행렬 4칸 전부).
      if (
        typeof parsed === 'object' &&
        parsed !== null &&
        (parsed as Record<string, unknown>)['type'] === 'server.caps'
      ) {
        const data = (parsed as Record<string, unknown>)['data']
        const audio_otf1 =
          typeof data === 'object' && data !== null &&
          (data as Record<string, unknown>)['audio_otf1'] === true
        otf1Armed = audio_otf1
        // D2 — 서버 VAD 에너지 바닥. 숫자·유한만 실어 올린다(구 서버는 이
        // 필드를 안 보내고, 깨진 값으로 화면 문턱을 흔들지 않는다).
        const floor =
          typeof data === 'object' && data !== null
            ? (data as Record<string, unknown>)['vad_energy_floor_dbfs']
            : undefined
        handlers.onCaps?.({
          audio_otf1,
          ...(typeof floor === 'number' && Number.isFinite(floor)
            ? { vad_energy_floor_dbfs: floor }
            : {}),
        })
        return // 방 이벤트가 아니다 — 리듀서에 흘리지 않는다(pong 과 동일 취급).
      }
      if (!isEnvelope(parsed)) return
      if (parsed.type === 'pong') {
        // **업링크 생존의 유일한 증거**(A1). 여기서만 pong 시계를 민다.
        lastPongAt = Date.now()
        pongSupported = true
        handlers.onPong?.(parsed.data as PongData)
        return // 방 이벤트가 아니다 — 리듀서에 흘리지 않는다.
      }
      if (parsed.seq !== null && parsed.seq > lastSeq) lastSeq = parsed.seq
      handlers.onEvent(parsed)
    }

    ws.onclose = (event: CloseEvent) => {
      socket = null
      // A.6 — 소켓이 끊기면 이 소켓의 캡 판정도 함께 죽는다(rollback 안전).
      otf1Armed = false
      if (watch) {
        clearInterval(watch)
        watch = null
      }
      if (closed) {
        handlers.onState('terminated')
        return
      }
      // §9 — 4409/replaced: 같은 키의 새 연결이 이 소켓을 대체했다. 재시도 금지.
      if (event.code === 4409) {
        closed = true
        handlers.onState('replaced')
        return
      }
      // §10.1 — 4401: 핸드셰이크 인증 실패. **같은 자격**의 재시도는
      // 소용없지만, 토큰 만료(§5.2 TTL 10분)가 원인일 수 있다 — 재교환
      // 콜백이 있으면 새 자격으로 딱 한 번 다시 붙어 본다(절전 복귀·
      // 만료 경계 재접속의 자기 복구 경로). 재교환이 null 을 주거나
      // 새 자격도 거부되면 그때 terminated 로 종결한다.
      if (event.code === 4401) {
        if (options.refreshKey && !authRetried) {
          authRetried = true
          void options.refreshKey().then((next) => {
            if (closed) return
            if (!next) {
              closed = true
              handlers.onState('terminated')
              return
            }
            handlers.onState('reconnecting')
            timer = setTimeout(open, backoffMs(attempt))
            attempt += 1
          })
          return
        }
        closed = true
        handlers.onState('terminated')
        return
      }
      // 방이 사라졌으면 재시도를 **멈춘다**(§9). 죽은 방을 향한 403 폭풍은
      // 서버 로그만 채우고 사용자에게는 아무것도 알려 주지 않는다.
      void roomLiveness(options.room, key).then((live) => {
        if (closed) return
        // 종료 **증거**가 있을 때만 room_closed 다. 판정 불가는 아래
        // 재접속 소진 경로로 흘러 room_gone(연결 실패 계열)이 된다.
        if (live === 'closed') {
          closed = true
          handlers.onState('room_closed')
          return
        }
        if (attempt >= MAX_RECONNECT_ATTEMPTS) {
          closed = true
          handlers.onState('room_gone')
          return
        }
        handlers.onState('reconnecting')
        timer = setTimeout(open, backoffMs(attempt))
        attempt += 1
      })
    }

    ws.onerror = () => {
      // onclose 가 뒤따르므로 여기서는 상태만 남긴다.
    }
  }

  open()

  return {
    send(data: ArrayBuffer): void {
      if (options.role !== 'host') return // §10.1 뷰어 인바운드 금지
      if (socket?.readyState === WebSocket.OPEN) socket.send(data)
    },
    sendControl(message: object): void {
      if (options.role !== 'host') return // §10.1 뷰어 인바운드 금지
      if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(message))
    },
    sendChat(body: string, name?: string): void {
      // §8.5 — 채팅은 §10.1 인바운드 전면 거부의 **좁은 예외**다.
      // 뷰어도 보낼 수 있는 유일한 메시지이므로 sendControl(host 전용)과
      // 분리한다. 서버가 크기·빈도를 다시 검사하므로 여기서는 보내기만 한다.
      if (socket?.readyState !== WebSocket.OPEN) return
      socket.send(JSON.stringify({ type: 'chat', body, ...(name ? { name } : {}) }))
    },
    get bufferedAmount(): number {
      return socket?.bufferedAmount ?? 0
    },
    get ready(): boolean {
      return socket?.readyState === WebSocket.OPEN
    },
    get otf1Armed(): boolean {
      return otf1Armed
    },
    close(): void {
      closed = true
      if (timer) clearTimeout(timer)
      if (watch) {
        clearInterval(watch)
        watch = null
      }
      socket?.close(1000, 'client close')
      handlers.onState('terminated')
    },
  }
}
